import { memo, useState } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
// import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import useConfig from '@hooks/useConfig';

import { HORIZONTAL_MAX_ITEM } from '@app/config';
import { useGetMenuMaster } from '@api/menu';

// types
import { NavItemType } from '@app/types';
import { MenuOrientation } from '@app/types/config';
import { useMenuItemsSelector } from '@hooks/useMenuItems';
import NavGroup from './NavGroup';
import NavItem from './NavItem';

// ==============================|| SIDEBAR MENU LIST ||============================== //

function MenuList() {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const items = useMenuItemsSelector((state) => state.items);
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  const isHorizontal =
    menuOrientation === MenuOrientation.HORIZONTAL && !downMD;
  const [selectedID, setSelectedID] = useState<string | undefined>('');

  // last menu-item to show in horizontal menu bar
  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;

  let lastItemIndex = items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string | undefined;

  if (lastItem && lastItem < items.length) {
    lastItemId = items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = items.slice(lastItem - 1, items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url,
      }),
    }));
  }

  const navItems = items.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={item.id}>
              <NavItem
                item={item}
                level={1}
                isParents
                setSelectedID={() => setSelectedID('')}
                customAction={item.customAction}
              />
              {!isHorizontal && index !== 0 && <Divider sx={{ py: 0.5 }} />}
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedID={setSelectedID}
            selectedID={selectedID}
            item={item}
            lastItem={lastItem!}
            remItems={remItems}
            lastItemId={lastItemId}
          />
        );
      default:
        // Render a plain NavItem for standalone items (e.g., ungrouped templates)
        return (
          <List key={item.id}>
            <NavItem
              item={item}
              level={1}
              isParents
              setSelectedID={() => setSelectedID('')}
              customAction={item.customAction}
            />
          </List>
        );
    }
  });

  return !isHorizontal ? (
    <Box {...(drawerOpen && { sx: { mt: 1.5 } })}>{navItems}</Box>
  ) : (
    <>{navItems}</>
  );
}

const MemoizedMenuList = memo(MenuList);
MemoizedMenuList.displayName = 'MenuList';

export default MemoizedMenuList;
